import { useState, type FormEvent, type ReactNode } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { invalidateServerAuthGateState } from "../../environments/primary/auth";
import {
  authErrorMessage,
  isExistingAccountError,
  signIn,
  signOut,
  signUpWithInvite,
  type AuthClientError,
  type SignUpWithInviteInput,
} from "../../shared/auth";
import {
  acceptFailureMessage,
  acceptInvite,
  existingAccountStep,
  fetchInviteSession,
  isExistingAccountInvite,
  signInAndJoin,
  type AcceptFailure,
  type ExistingAccountInvite,
  type SignInAndJoinOutcome,
} from "../../shared/auth/invite-accept";
import {
  INVITE_TOKEN_PATTERN,
  fetchInvitePreview,
  registrationEmail,
  type InvitePreview,
  type UnusableInviteStatus,
} from "../../shared/auth/invite-preview";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { onWebAddress, openAfterAccountChange } from "../../shared/auth/web-address";
import { PasswordInput } from "./PasswordInput";
import type { I18nKey } from "~/i18n";

/** Better Auth default minimum password length. */
const MIN_PASSWORD_LENGTH = 8;

const routeApi = getRouteApi("/register");

const UNUSABLE_INVITE: Record<UnusableInviteStatus, I18nKey> = {
  used: "common/thisInviteHasAlready_8m17gy",
  expired: "common/thisInviteHasExpired_57a2yy",
  invalid: "common/thisInviteIsNot_9zpc4p",
};

/**
 * The invite page, and invite-gated registration. A mailed invite link opens
 * it with `?invite=`: the invite is checked before anything is asked, and the
 * token stays out of sight. An invite belongs to its email: a new address
 * registers here, and an address that has an account signs in here and joins
 * the team, whoever was signed in to this browser before. In the desktop the
 * recipient pastes the token by hand, and a pasted token of the right shape is
 * checked the same way.
 */
export function RegisterPage() {
  const { t } = useTranslation();
  const { invite } = routeApi.useSearch();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [password, setPassword] = useState("");
  const [manualInvite, setManualInvite] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const inviteToken = invite ?? manualInvite.trim();
  const checksInvite = invite !== undefined || INVITE_TOKEN_PATTERN.test(inviteToken);
  const previewQuery = useQuery({
    queryKey: ["invite-preview", inviteToken],
    queryFn: () => fetchInvitePreview(inviteToken),
    enabled: checksInvite,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const preview = checksInvite ? previewQuery.data : undefined;
  const { email, emailLocked } = registrationEmail(preview, emailDraft);

  const mutation = useMutation<AuthClientError | null, never, SignUpWithInviteInput>({
    mutationFn: async (input) => {
      try {
        const { error } = await signUpWithInvite(input);
        return error ?? null;
      } catch {
        // better-fetch rethrows a network failure instead of returning it.
        return { code: null };
      }
    },
    onSuccess: (error) => {
      if (error === null) openAfterAccountChange("/", (to) => void navigate({ to }));
    },
  });
  const signUpError = mutation.data ?? null;

  if (invite !== undefined && previewQuery.isPending) {
    return (
      <RegisterCard title={t("auth/register_ugub6a")}>
        <p className="text-muted-foreground text-sm" role="status">
          {t("auth/checkingTheInvite_603dbp")}
        </p>
      </RegisterCard>
    );
  }

  if (
    invite !== undefined &&
    preview !== undefined &&
    preview.status !== "pending" &&
    preview.status !== "unavailable"
  ) {
    return (
      <RegisterCard
        title={t(UNUSABLE_INVITE[preview.status])}
        description={t("auth/askThePersonWho_ud9r88")}
      >
        <Button render={<Link to="/sign-in" />}>{t("auth/signIn_hq86wm")}</Button>
      </RegisterCard>
    );
  }

  // A registered person's invite is mailed as a link to this page; a code
  // pasted by hand stays on the form, which sends an existing account to sign-in.
  if (invite !== undefined && isExistingAccountInvite(preview)) {
    return <ExistingAccountInvitePage invite={preview} token={invite} />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(t("auth/passwordMustBeAt_7huopp"));
      return;
    }
    if (inviteToken.length === 0) {
      setValidationError(t("auth/enterAnInviteToken_xiqrh0"));
      return;
    }
    setValidationError(null);
    mutation.mutate({ email, password, name: name.trim(), inviteToken });
  };

  const errorMessage = validationError ?? (signUpError ? authErrorMessage(signUpError) : null);
  const heading = inviteHeading(preview);
  const team = preview?.status === "pending" ? preview.team : null;

  return (
    <RegisterCard
      title={heading === null ? t("auth/register_ugub6a") : t(heading.key, heading.values)}
      description={
        heading !== null || team === null ? undefined : t("auth/youAreInvitedTo_26ty60", { team })
      }
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {invite !== undefined && preview?.status === "unavailable" ? (
          <div className="flex flex-col items-start gap-2 text-sm" role="alert">
            <p className="text-muted-foreground">{t("auth/couldNotCheckThe_w45ay1")}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={previewQuery.isFetching}
              onClick={() => void previewQuery.refetch()}
            >
              {previewQuery.isFetching
                ? t("auth/checkingTheInvite_603dbp")
                : t("common/tryAgain_lshgks")}
            </Button>
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-name">{t("common/name_p0qla7")}</Label>
          <Input
            id="register-name"
            autoComplete="name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-email">{t("auth/emailAddress_1m22un")}</Label>
          <Input
            id="register-email"
            type="email"
            autoComplete="email"
            required
            readOnly={emailLocked}
            className={emailLocked ? "bg-muted" : undefined}
            value={email}
            onChange={(event) => setEmailDraft(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-password">{t("auth/password_dn5241")}</Label>
          <PasswordInput
            id="register-password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        {invite === undefined ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="register-invite">{t("auth/inviteToken_tnyzbg")}</Label>
            <Input
              id="register-invite"
              required
              value={manualInvite}
              onChange={(event) => setManualInvite(event.target.value)}
            />
            {preview !== undefined &&
            preview.status !== "pending" &&
            preview.status !== "unavailable" ? (
              <p className="text-destructive text-sm">{t(UNUSABLE_INVITE[preview.status])}</p>
            ) : null}
          </div>
        ) : null}
        {errorMessage ? (
          <div className="flex flex-col items-start gap-2" role="alert">
            <p className="text-destructive text-sm">{errorMessage}</p>
            {validationError === null && isExistingAccountError(signUpError) ? (
              <Button variant="outline" size="sm" render={<Link to="/sign-in" />}>
                {t("auth/signIn_hq86wm")}
              </Button>
            ) : null}
          </div>
        ) : null}
        <Button type="submit" disabled={mutation.isPending || mutation.data === null}>
          {mutation.isPending ? t("auth/creatingAccount_68eh5e") : t("auth/createAccount_trehaw")}
        </Button>
        <p className="text-muted-foreground text-sm">
          {t("auth/alreadyHaveAnAccount_rmqlwt")}{" "}
          <Link className="text-primary underline-offset-4" to="/sign-in">
            {t("auth/signIn_hq86wm")}
          </Link>
        </p>
      </form>
    </RegisterCard>
  );
}

/** The heading of a usable invite that names its address. */
function inviteHeading(
  preview: InvitePreview | undefined,
): { readonly key: I18nKey; readonly values: Record<string, string> } | null {
  if (preview?.status !== "pending" || preview.email === null) return null;
  return preview.team === null
    ? { key: "auth/inviteToDovodFor_70mxdq", values: { email: preview.email } }
    : { key: "auth/inviteToTheOrganization_3gu3cn", values: { team: preview.team } };
}

const SIGN_IN_AND_JOIN_FLOW = {
  signOut: () => signOut(),
  invalidateGateState: invalidateServerAuthGateState,
  signIn: (input: { readonly email: string; readonly password: string }) =>
    signIn.email({ email: input.email, password: input.password }),
  accept: (token: string) => acceptInvite(token),
};

/**
 * An invite into a team for an email that already has an account. The invite
 * belongs to that email, so the page signs in as it, with its password, and
 * joins; an account already signed in as it joins with one button.
 */
function ExistingAccountInvitePage(props: {
  readonly invite: ExistingAccountInvite;
  readonly token: string;
}) {
  const { invite, token } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const title = t("auth/inviteToTheOrganization_3gu3cn", { team: invite.team });

  const [password, setPassword] = useState("");
  const [signInError, setSignInError] = useState<string | null>(null);
  const [acceptFailure, setAcceptFailure] = useState<AcceptFailure | null>(null);
  const [joined, setJoined] = useState<{ readonly name: string }>();

  const sessionQuery = useQuery({
    queryKey: ["invite-session"],
    queryFn: () => fetchInviteSession(),
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const step = existingAccountStep(invite, sessionQuery.data);

  const settle = (outcome: SignInAndJoinOutcome) => {
    if (outcome.kind === "joined") {
      setJoined(outcome.team);
      // The server token names the organisation, which joining changed.
      invalidateServerAuthGateState();
      openAfterAccountChange("/", (to) => void navigate({ to }));
      return;
    }
    if (outcome.kind === "sign-in-failed") {
      setSignInError(authErrorMessage(outcome.error));
      // A sign-out before the sign-in may have ended another account's session.
      void sessionQuery.refetch();
      return;
    }
    setAcceptFailure(outcome.failure);
    // Signed in as the invited account now, or the session ended: either way
    // the page asks the session again.
    void sessionQuery.refetch();
  };

  const signInMutation = useMutation<SignInAndJoinOutcome, never, string | null>({
    mutationFn: (signedInAs) =>
      signInAndJoin(SIGN_IN_AND_JOIN_FLOW, {
        token,
        email: invite.email,
        password,
        signedInAs,
      }),
    onMutate: () => {
      setSignInError(null);
      setAcceptFailure(null);
    },
    onSuccess: settle,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const result = await acceptInvite(token);
      return result.ok
        ? ({ kind: "joined", team: result.team } as const)
        : ({ kind: "accept-failed", failure: result.failure } as const);
    },
    onMutate: () => setAcceptFailure(null),
    onSuccess: settle,
  });

  if (joined !== undefined) {
    return (
      <RegisterCard title={t("auth/youAreInThe_r5v9az", { team: joined.name })}>
        <p className="text-muted-foreground text-sm" role="status">
          {t("auth/openingYourOrganizationS_s46i31")}
        </p>
      </RegisterCard>
    );
  }

  if (
    step.kind === "already-member" ||
    (acceptFailure?.kind === "refused" && acceptFailure.code === "ALREADY_MEMBER")
  ) {
    return (
      <RegisterCard title={t("auth/youAreAlreadyIn_y50r04", { team: invite.team })}>
        <Button render={onWebAddress() ? <a href="/" /> : <Link to="/" />}>
          {t("auth/goToTheOrganization_5hkksx")}
        </Button>
      </RegisterCard>
    );
  }

  if (step.kind === "checking") {
    return (
      <RegisterCard title={title}>
        <p className="text-muted-foreground text-sm" role="status">
          {t("auth/checkingTheInvite_603dbp")}
        </p>
      </RegisterCard>
    );
  }

  if (step.kind === "unavailable") {
    return (
      <RegisterCard title={title}>
        <div className="flex flex-col items-start gap-2 text-sm" role="alert">
          <p className="text-muted-foreground">{t("auth/couldNotCheckYour_opsanu")}</p>
          <Button
            variant="outline"
            size="sm"
            disabled={sessionQuery.isFetching}
            onClick={() => void sessionQuery.refetch()}
          >
            {t("common/tryAgain_lshgks")}
          </Button>
        </div>
      </RegisterCard>
    );
  }

  const moving =
    step.movingFrom === null ? null : (
      <p className="text-muted-foreground text-sm">
        {t("auth/youWillLeaveThe_26m4sv", { from: step.movingFrom })}
      </p>
    );
  const failure =
    acceptFailure === null ? null : (
      <p className="text-destructive text-sm" role="alert">
        {acceptFailureMessage(acceptFailure)}
      </p>
    );

  if (step.kind === "join") {
    return (
      <RegisterCard title={title}>
        {failure}
        <Button disabled={acceptMutation.isPending} onClick={() => acceptMutation.mutate()}>
          {acceptMutation.isPending
            ? t("auth/acceptingTheInvite_3frvmu")
            : t("auth/joinTheOrganization_o9rw6n")}
        </Button>
        {moving}
      </RegisterCard>
    );
  }

  const handleSignIn = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    signInMutation.mutate(step.signedInAs);
  };

  return (
    <RegisterCard title={title}>
      <form className="flex flex-col gap-4" onSubmit={handleSignIn}>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-email">{t("auth/emailAddress_1m22un")}</Label>
          <Input
            id="invite-email"
            type="email"
            autoComplete="username"
            readOnly
            className="bg-muted"
            value={invite.email}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor="invite-password">{t("auth/password_dn5241")}</Label>
            <Link
              className="text-primary text-sm underline-offset-4 hover:underline"
              to="/forgot-password"
            >
              {t("auth/forgotYourPassword_ws302f")}
            </Link>
          </div>
          <PasswordInput
            id="invite-password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        {signInError === null ? (
          failure
        ) : (
          <p className="text-destructive text-sm" role="alert">
            {signInError}
          </p>
        )}
        <Button type="submit" disabled={signInMutation.isPending}>
          {signInMutation.isPending ? t("auth/signingIn_gxau9a") : t("auth/signInAndJoin_gnccn9")}
        </Button>
        {moving}
      </form>
    </RegisterCard>
  );
}

function RegisterCard(props: {
  readonly title: string;
  readonly description?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{props.title}</CardTitle>
          {props.description === undefined ? null : (
            <CardDescription>{props.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">{props.children}</CardContent>
      </Card>
    </main>
  );
}
